import { useNotificationStore } from '@/stores/notification';
import { V1UserFeedPayload } from '@4csoftware/api-types';
import { emitter } from '../emitter';
import { useUserStore } from '@/stores/user';
import { handlePositionUpdate } from './handlePositionUpdate';

export function handleUserFeedV1SocketMessage(payload: string) {
  const notificationStore = useNotificationStore();
  const userStore = useUserStore();

  const parsedPayload = JSON.parse(payload) as V1UserFeedPayload;
  switch (parsedPayload.type) {
    case 'ack-notifications': {
      console.log('Acking notifications', parsedPayload);
      parsedPayload.data.notificationIDs.forEach((notificationID) => {
        notificationStore.markInboxNotificationAsRead(notificationID, { updateBackend: false });
      });
      break;
    }
    case 'cancellation': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'default' });
      break;
    }
    case 'edit': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'default' });
      break;
    }
    case 'multiple-cancels': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'default' });
      break;
    }
    // no ack ironically
    case 'notification': {
      notificationStore.addAlert(parsedPayload.alert);
      break;
    }
    case 'order-fill-notification': {
      // Stash routing context + createdAt on the alert content so the toast can
      // route to the game's exchange grid on click and render a relative
      // timestamp. sport/league let us push directly to /exchange/<sport>/
      // <league>; awayRotationNumber lets that page ?search= down to the game.
      // The data type only declares { gameID }, so we widen via a cast — the
      // extra fields are read defensively in the UI.
      const inboxNotification = parsedPayload.inboxNotification;
      const fillData = parsedPayload.data as
        | {
            gameID?: string;
            sport?: string;
            league?: string;
            awayRotationNumber?: string;
          }
        | undefined;
      const enriched = {
        ...inboxNotification,
        content: {
          ...inboxNotification.content,
          gameID: fillData?.gameID,
          sport: fillData?.sport,
          league: fillData?.league,
          awayRotationNumber: fillData?.awayRotationNumber,
          createdAt: inboxNotification.createdAt,
        },
      } as typeof inboxNotification;
      notificationStore.addInboxNotification(enriched, { alertType: 'priority' });
      userStore.getMe();
      break;
    }
    case 'new-player': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'priority' });
      break;
    }
    // no ack
    case 'cancelled-seed': {
      notificationStore.addAlert(parsedPayload.alert);
      break;
    }
    case 'deposit-confirmed': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'priority' });
      userStore.getMe();
      emitter.emit('depositConfirmed');
      break;
    }
    // no ack
    case 'update-offer': {
      notificationStore.addAlert(parsedPayload.alert);
      break;
    }
    case 'banner-message': {
      notificationStore.addBanner(parsedPayload.banner);
      userStore.getMe();
      break;
    }
    case 'admin-banner-message': {
      notificationStore.addBanner(parsedPayload.banner);
      userStore.getMe();
      break;
    }
    case 'position-update': {
      handlePositionUpdate(parsedPayload.data);
      break;
    }
    case 'alert': {
      notificationStore.addAlert(parsedPayload.alert);
      break;
    }
    case 'priority-alert': {
      notificationStore.addPriorityAlert(parsedPayload.alert);
      break;
    }
    case 'notification-with-no-alert': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'none' });
      break;
    }
    case 'notification-with-alert': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'default' });
      break;
    }
    case 'notification-with-priority-alert': {
      notificationStore.addInboxNotification(parsedPayload.inboxNotification, { alertType: 'priority' });
      break;
    }
    default: {
      console.error('Unknown user feed v1 message type:', parsedPayload);
      break;
    }
  }
}